import Diff from 'deep-diff';
import QueryString from 'query-string';
import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState
} from 'react';
import { useTrackVisibility } from 'react-intersection-observer-hook';

import {
    useEffectOnUpdate,
    usePrevious
} from './data';
import Util from '$utilities';

/* =========================================================================  */
/* Hook(s) */
/* =========================================================================  */

/**
 * @hook useAjaxListing
 * @option {boolean} infinite (optional)
 * @option {array} items (optional)
 * @option {number} page (optional)
 * @option {string} path (optional)
 * @option {object} query (optional)
 * @option {number} totalItems (optional)
 * @option {number} totalPages (optional)
 */
const useAjaxListing = (options = {}) => {
    const [ url, updateUrl ] = useReducer((state, data) => {
        if (data.page) {
            Util.updateHistoryState('page', data.page);
        }
        if (data.query) {
            for (let key in data.query) {
                Util.updateHistoryState(key, data.query[key]);
            }
        }
        return {
            ...state,
            ...data
        };
    }, {
        page: options.page || 1,
        query: options.query,
        path: options.path
    });
    const [ items, setItems ] = useState(options.items || []);
    const [ loading, setLoading ] = useState(false);
    const [ status, setStatus ] = useState(false);
    const [ totalItems, setTotalItems ] = useState(options.totalItems || 0);
    const [ totalPages, setTotalPages ] = useState(options.totalPages || 1);

    const previousQuery = usePrevious(options.query);
    const previousUrl = usePrevious(url);

    const setPage = useCallback((target) => {
        updateUrl({page: target});
    }, []);

    const controller = useRef();

    /**
     * @function load
     * Make an AJAX request for more items
     */
    const load = async () => {
        (loading && controller.current) && controller.current.abort();

        try {
            setLoading(true);

            let signal;
            if (window && window.AbortController) {
                controller.current = new window.AbortController();
                signal = controller.current.signal;
            }

            const queryString = (() => {
                let queryParameters = { page: url.page };
                if (url.query) {
                    Object.keys(url.query).forEach((k) => {
                        if (Array.isArray(url.query[k]) ? url.query[k].length > 0 && url.query[k][0] !== null : url.query[k] !== null) {
                            queryParameters[k] = url.query[k];
                        }
                    });
                }
                return `?${QueryString.stringify(queryParameters)}`;
            })();

            const response = await fetch(`${url.path}${queryString}`, {
                signal: signal
            });

            if (response) {
                if (response.status !== 200) {
                    setLoading(false);
                    setStatus(response.status);
                    return;
                }

                const data = await response.json();

                if (typeof options.onData === 'function') {
                    options.onData(data);
                }

                setItems(options.infinite && url.page > 1 ? items.concat(data.items) : data.items);
                setLoading(false);
                setStatus(200);
                setTotalItems(data.totalItems);
                setTotalPages(data.totalPages);
            }
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                return;
            }
            console.error('[AjaxListing]', err);
            setStatus(500);
        }
    };

    useEffectOnUpdate(() => {
        if (Diff(previousQuery, options.query)) {
            updateUrl({
                query: options.query,
                page: 1
            });
        }
    }, [options.query]);

    useEffectOnUpdate(() => {
        updateUrl({
            path: options.path,
            page: 1
        });
    }, [options.path]);

    useEffectOnUpdate(() => {
        if (Diff(previousUrl, url)) {
            load();
        }
    }, [url]);

    return useMemo(() => ({
        items,
        loading,
        page: url.page,
        setPage,
        status,
        totalItems,
        totalPages
    }), [
        items,
        loading,
        setPage,
        status,
        totalItems,
        totalPages,
        url.page
    ]);
};

/**
 * @hook useInfiniteScroll
 * @option {boolean} disabled (optional)
 * @option {boolean} loading (optional)
 * @option {function} hasNextPage (optional)
 * @option {function} onLoad (optional)
 * @option {number} threshold (optional)
 */
const useInfiniteScroll = (options = {}) => {
    const disabled = options.disabled || false;
    const hasNextPage = options.hasNextPage || false;
    const loading = options.loading || false;
    const onLoad = options.onLoad || null;
    const threshold = options.threshold || 500;

    const [ref, { isVisible }] = useTrackVisibility({
        rootMargin: `${threshold}px`
    });

    useEffect(() => {
        const readyToLoad = !disabled && !loading && hasNextPage && isVisible;

        if (readyToLoad && typeof onLoad === 'function') {
            const timer = setTimeout(() => {
                onLoad();
            }, 300);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [
        disabled,
        hasNextPage,
        isVisible,
        loading,
        onLoad
    ]);

    return ref;
};

/* =========================================================================  */
/* Exports */
/* =========================================================================  */

export {
    useAjaxListing,
    useInfiniteScroll
};
